import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, onSnapshot, collection, getDoc, addDoc, updateDoc } from 'firebase/firestore';
import { useOrganization } from '../context/OrganizationContext';
import PageHeader from '../components/PageHeader';
import { useTranslation } from 'react-i18next';
import { SELECTABLE_TIMEZONES } from '../utils/timezoneMap';
import { zonaEfectiva } from '../utils/zonaEfectiva';

// Components
import BotStatusBanner from '../componentes/bot/BotStatusBanner';
import PlaybookEditor from '../componentes/bot/PlaybookEditor';
import ToolsAllowlist from '../componentes/bot/ToolsAllowlist';
import PoliciesForm from '../componentes/bot/PoliciesForm';
import BotIntegrationsPanel from '../componentes/bot/BotIntegrationsPanel';
import KnowledgeForm from '../componentes/bot/KnowledgeForm';
import { limitesDelConocimiento } from '../utils/limitesDelConocimiento';
import KnowledgeDocuments from '../componentes/bot/KnowledgeDocuments';
import BibliotecaDeMedios from '../componentes/bot/BibliotecaDeMedios';

// Sales Engine
import { getSalesFramework, getAllSalesFrameworks } from '../ai/salesFrameworks';
import { getFunnelTemplate, getAllIndustries } from '../utils/funnelTemplates';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
  "https://us-central1-sistema-bks.cloudfunctions.net";

// Los órdenes posibles de los archivos (`config.ordenDeArchivos`): las seis permutaciones de los tipos que valida
// `ordenDeTipos` en `functions/bot/media/envioDeArchivos.js`; el primero es el de siempre. Paridad en
// `functions/__tests__/ordenDeArchivos.jest.test.js`.
const ORDENES_DE_ARCHIVOS = [
  ['pdf', 'imagen', 'audio'],
  ['imagen', 'pdf', 'audio'],
  ['pdf', 'audio', 'imagen'],
  ['imagen', 'audio', 'pdf'],
  ['audio', 'pdf', 'imagen'],
  ['audio', 'imagen', 'pdf'],
];

export default function BotConfig() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const { context, loading: contextLoading } = useOrganization();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [config, setConfig] = useState(null);
  const [tenantPolicy, setTenantPolicy] = useState(null);
  const [knowledge, setKnowledge] = useState(null);
  const [playbooks, setPlaybooks] = useState([]);
  const [runtime, setRuntime] = useState(null);
  // La zona de la organización: la que hereda un bot sin `config.timeZone` (la pantalla la enseña, no la guarda).
  const [zonaDeLaOrg, setZonaDeLaOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  // CONFIRMAR ANTES DE GUARDAR UN CONOCIMIENTO POR ENCIMA DEL TOPE (15-oct-2026,
  // `utils/limitesDelConocimiento.js`): en la misma página, sin `confirm()`.
  const [confirmarLimites, setConfirmarLimites] = useState(false);
  // Ediciones sin guardar: congela el snapshot (no pisa config/tenantPolicy/
  // knowledge) y alimenta el badge + beforeunload. runtime NUNCA se congela.
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
  const [message, setMessage] = useState(null);
  const [accessValidated, setAccessValidated] = useState(false);
  const [refreshIntegrations, setRefreshIntegrations] = useState(0);
  const { t } = useTranslation('bots');
  const { t: tc } = useTranslation('common');

  // Permission checks
  const canEditBots = context.member?.role === 'owner' || context.member?.permissions?.canEditBots;

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(u => setUser(u));
    return () => unsub();
  }, []);

  // Validate access to bot based on organization context
  useEffect(() => {
    if (!user || !botId || contextLoading) return;

    const validateAccess = async () => {
      try {
        // Query bot from correct path based on org context
        let botRef;
        if (context.mode === 'organization' && context.orgId) {
          botRef = doc(db, 'organizations', context.orgId, 'bots', botId);
        } else {
          botRef = doc(db, 'clientes', user.uid, 'bots', botId);
        }

        const botDoc = await getDoc(botRef);

        if (!botDoc.exists()) {
          alert(t('config.botNotFound'));
          navigate('/bots');
          return;
        }

        // Access validation is implicit in the path - if bot exists at this path, user has access
        if (context.mode === 'organization' && !canEditBots) {
          alert(t('config.noPermissionEdit'));
          navigate('/bots');
          return;
        }

        setAccessValidated(true);
      } catch (error) {
        console.error('Error validating bot access:', error);
        alert(t('config.accessError'));
        navigate('/bots');
      }
    };

    validateAccess();
  }, [user, botId, context, contextLoading, canEditBots, navigate]);

  // LA ZONA DE LA ORGANIZACIÓN (2-nov-2026, `utils/zonaEfectiva.js`): sin ella, el selector enseñaba Santiago a un bot
  // que en el servidor heredaba otra.
  useEffect(() => {
    if (!accessValidated || context.mode !== 'organization' || !context.orgId) { setZonaDeLaOrg(null); return; }
    let vivo = true;
    getDoc(doc(db, 'organizations', context.orgId))
      .then((s) => { if (vivo) setZonaDeLaOrg(s.exists() ? (s.data()?.timeZone || null) : null); })
      .catch(() => { if (vivo) setZonaDeLaOrg(null); });
    return () => { vivo = false; };
  }, [accessValidated, context.mode, context.orgId]);

  // Load bot data once access is validated
  useEffect(() => {
    if (!user || !botId || !accessValidated) return;

    // Query bot from correct path based on org context
    let botRef;
    if (context.mode === 'organization' && context.orgId) {
      botRef = doc(db, 'organizations', context.orgId, 'bots', botId);
    } else {
      botRef = doc(db, 'clientes', user.uid, 'bots', botId);
    }

    const unsub = onSnapshot(botRef, snap => {
      if (snap.exists()) {
        const data = snap.data();
        // runtime SIEMPRE se refresca (lo escriben activateBot y el borrado
        // de playbooks, y el banner lo necesita al día). Los estados editables
        // solo si no hay ediciones pendientes — antes cualquier escritura
        // externa al doc pisaba silenciosamente el trabajo sin guardar.
        setRuntime(data.runtime || null);
        if (!dirtyRef.current) {
          setConfig(data.config || {});
          setTenantPolicy(data.tenantPolicy || {});
          setKnowledge(data.knowledge || {});
        }
      }
    });

    return () => unsub();
  }, [user, botId, accessValidated, context]);

  // Load playbooks once access is validated
  useEffect(() => {
    if (!user || !botId || !accessValidated) return;

    // Query playbooks from correct path based on org context
    let playbooksRef;
    if (context.mode === 'organization' && context.orgId) {
      playbooksRef = collection(db, 'organizations', context.orgId, 'bots', botId, 'playbooks');
    } else {
      playbooksRef = collection(db, 'clientes', user.uid, 'bots', botId, 'playbooks');
    }

    const unsub = onSnapshot(playbooksRef, snap => {
      const pbs = [];
      snap.forEach(d => pbs.push({ id: d.id, ...d.data() }));
      setPlaybooks(pbs);
    });

    return () => unsub();
  }, [user, botId, accessValidated, context]);

  // Check for URL message parameter and OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const messageParam = urlParams.get('message');
    const calendarConnected = urlParams.get('calendar_connected');

    if (messageParam) {
      setMessage({ type: 'info', text: messageParam });
    }

    if (calendarConnected === 'true') {
      setMessage({ type: 'success', text: t('config.calendarConnected') });
      // CRITICAL: Force integrations panel to refresh
      setRefreshIntegrations(prev => prev + 1);
    }

    // Clean URL without reloading page
    if (messageParam || calendarConnected) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const updateConfig = (key, value) => {
    setDirty(true);
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const updatePolicy = (key, value) => {
    setDirty(true);
    setTenantPolicy(prev => ({ ...prev, [key]: value }));
  };

  const saveConfig = async () => {
    if (!user || !botId) return;

    if (!config) {
      setMessage({ type: 'error', text: 'Error: config is null' });
      return;
    }

    console.log('[BotConfig] Saving config:', {
      hasConfig: !!config,
      configKeys: config ? Object.keys(config) : [],
      configName: config?.name,
      hasTenantPolicy: !!tenantPolicy,
      hasKnowledge: !!knowledge,
      organizationId: context.mode === 'organization' ? context.orgId : null
    });

    try {
      setSaving(true);
      setMessage(null);
      const idToken = await user.getIdToken();

      const res = await fetch(`${API_URL}/saveBotConfigPlaybooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          botId,
          config,
          tenantPolicy,
          knowledge,
          organizationId: context.mode === 'organization' ? context.orgId : null,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setDirty(false);
        setMessage({ type: 'success', text: t('config.savedOk') });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: t('config.saveError') });
      }
    } catch (error) {
      console.error('Error saving config:', error);
      setMessage({ type: 'error', text: t('config.saveConfigError') });
    } finally {
      setSaving(false);
    }
  };

  const validatePlaybook = async (playbookId) => {
    const playbook = playbooks.find(p => p.id === playbookId);
    if (!playbook) return;

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/validatePlaybook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ playbook }),
      });

      const result = await res.json();
      if (result.valid) {
        setMessage({ type: 'success', text: t('config.playbookValid') });
      } else {
        setMessage({
          type: 'error',
          text: `Errores: ${result.errors.join(', ')}`,
        });
      }
    } catch (error) {
      console.error('Error validating playbook:', error);
      setMessage({ type: 'error', text: t('config.playbookValidateError') });
    }
  };

  const activateBot = async (playbookId = null) => {
    // Con playbookId activa ESE flujo; sin argumento (banner) cae al primer
    // funnel guardado con estados.
    const activePlaybook = playbookId
      ? playbooks.find(p => p.id === playbookId)
      : playbooks.find(p => p.states && Object.keys(p.states).length > 0);
    if (!activePlaybook) {
      setMessage({ type: 'error', text: t('config.noFunnel') });
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/activateBot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          botId,
          playbookId: activePlaybook.id,
          organizationId: context.mode === 'organization' ? context.orgId : null,
        }),
      });

      const result = await res.json();
      if (result.success) {
        setMessage({ type: 'success', text: t('config.botActivated') });
      }
    } catch (error) {
      console.error('Error activating bot:', error);
      setMessage({ type: 'error', text: t('config.activateError') });
    }
  };

  // Al borrar un playbook, nunca dejar el bot sin guión en silencio: si el
  // puntero activo quedó apuntando al borrado (o ya estaba huérfano), se
  // limpia; si queda exactamente un flujo con estados, se ofrece promoverlo.
  const handlePlaybookDeleted = async (deletedId) => {
    setMessage({ type: 'success', text: t('config.funnelDeleted') });

    const remaining = playbooks.filter(p => p.id !== deletedId);
    const pointerBroken = runtime?.activePlaybookId &&
      !remaining.some(p => p.id === runtime.activePlaybookId);
    if (!pointerBroken) return;

    try {
      const botRef = context.mode === 'organization' && context.orgId
        ? doc(db, 'organizations', context.orgId, 'bots', botId)
        : doc(db, 'clientes', user.uid, 'bots', botId);
      await updateDoc(botRef, {
        'runtime.activePlaybookId': null,
        'runtime.currentState': null,
      });

      const candidates = remaining.filter(p => p.states && Object.keys(p.states).length > 0);
      if (candidates.length === 1 &&
          window.confirm(t('config.playbooks.promoteRemaining', { name: candidates[0].name }))) {
        await activateBot(candidates[0].id);
      }
    } catch (error) {
      console.error('Error limpiando el puntero de playbook activo:', error);
    }
  };

  const createPlaybook = async () => {
    try {
      const { addDoc, collection } = await import('firebase/firestore');
      const { db } = await import('../firebase');

      const isOrgContext = context.mode === 'organization' && context.orgId;

      // 🚨 IMPORTANT: All resources MUST include organizationId field for Firestore security rules
      const newPlaybook = {
        name: 'Nuevo Playbook',
        start_state: 'inicio',
        strict: false,
        active: false,
        states: {
          inicio: {
            ask: '¿En qué puedo ayudarte?',
            capture: [],
            next: 'fin'
          },
          fin: {
            ask: 'Gracias por contactarnos',
            terminal: true
          }
        },
        stateOrder: ['inicio', 'fin'], // PHASE 3 FIX: Explicit order for funnel stages
        createdAt: new Date(),
        organizationId: isOrgContext ? context.orgId : null,
      };

      const playbooksRef = isOrgContext
        ? collection(db, 'organizations', context.orgId, 'bots', botId, 'playbooks')
        : collection(db, `clientes/${user.uid}/bots/${botId}/playbooks`);
      await addDoc(playbooksRef, newPlaybook);

      setMessage({ type: 'success', text: t('config.playbookCreated') });
    } catch (error) {
      console.error('Error creating playbook:', error);
      setMessage({ type: 'error', text: t('config.playbookCreateError') });
    }
  };

  const applyTemplate = async (templateId) => {
    try {
      const template = getFunnelTemplate(templateId);
      if (!template) {
        setMessage({ type: 'error', text: t('config.templateNotFound') });
        return;
      }

      // Confirmación explícita: aplicar pisa flujos, tools, estrategia y tipo
      // de bot — antes se ejecutaba en silencio con un click (tanda B, bug 2).
      let confirmText = t('config.playbooks.applyConfirm', { name: template.name });
      if (/\[[XY]\]/.test(JSON.stringify(template.playbook.states || {}))) {
        confirmText += '\n\n' + t('config.playbooks.applyConfirmPending');
      }
      if (!window.confirm(confirmText)) return;

      setSaving(true);

      // Determine correct paths based on context
      let playbooksRef;
      let botRef;
      if (context.mode === 'organization' && context.orgId) {
        playbooksRef = collection(db, 'organizations', context.orgId, 'bots', botId, 'playbooks');
        botRef = doc(db, 'organizations', context.orgId, 'bots', botId);
      } else {
        playbooksRef = collection(db, `clientes/${user.uid}/bots/${botId}/playbooks`);
        botRef = doc(db, `clientes/${user.uid}/bots`, botId);
      }

      // 1. Desactivar todos los playbooks existentes
      const { getDocs, writeBatch } = await import('firebase/firestore');
      const existingPlaybooks = await getDocs(playbooksRef);
      const batch = writeBatch(db);

      existingPlaybooks.forEach(doc => {
        batch.update(doc.ref, { active: false });
      });

      // 2. Crear nuevo playbook basado en template.
      //    Tools son bot-level (tenantPolicy.tools_allowed); se sacan del playbook.
      const { tools_allowed: templateTools = [], ...playbookWithoutTools } = template.playbook;

      // Sustituir los placeholders de nombre del negocio al copiar las etapas.
      // [FECHA]/[HORA] quedan (slots que el LLM rellena en conversación) y
      // [X]/[Y] también (datos que solo el negocio conoce; se completan en el editor).
      const NAME_PLACEHOLDER_RE = /\[(NOMBRE_CLINICA|CLINICA|NOMBRE_RESTAURANTE|NOMBRE_BUFETE|NOMBRE_TALLER)\]/g;
      const businessName = knowledge?.company_name || config?.name || '';
      const fillBusinessName = (value) => {
        if (typeof value === 'string') return value.replace(NAME_PLACEHOLDER_RE, businessName);
        if (Array.isArray(value)) return value.map(fillBusinessName);
        if (value && typeof value === 'object') {
          return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, fillBusinessName(v)]));
        }
        return value;
      };

      const newPlaybook = {
        ...(businessName ? fillBusinessName(playbookWithoutTools) : playbookWithoutTools),
        active: true,
        createdAt: new Date(),
        organizationId: context.mode === 'organization' ? context.orgId : null,
        metadata: {
          source: 'template',
          templateId: templateId,
          appliedAt: new Date()
        }
      };

      const newPlaybookRef = await addDoc(playbooksRef, newPlaybook);

      // 3. Set the canonical tools list from the template (always), plus the
      //    recommended sales framework if the template defines one.
      const botUpdate = { 'tenantPolicy.tools_allowed': templateTools };
      const recommendedFramework = template.recommendedSalesFramework;
      if (recommendedFramework) {
        Object.assign(botUpdate, {
          'config.salesFramework': recommendedFramework,
          'config.industry': template.industry,
          'runtime.activePlaybookId': newPlaybookRef.id,
          'runtime.currentState': template.playbook.start_state,
          'runtime.turnCount': 0,
          'tipo': 'playbook'
        });
        setConfig(prev => ({
          ...prev,
          salesFramework: recommendedFramework,
          industry: template.industry
        }));
      }
      batch.update(botRef, botUpdate);

      // 4. Commit all changes
      await batch.commit();

      const frameworkName = getSalesFramework(recommendedFramework)?.name || 'SPIN Selling';
      setMessage({
        type: 'success',
        text: t('config.templateApplied', { name: template.name, framework: frameworkName })
      });

      setSaving(false);
    } catch (error) {
      console.error('Error applying template:', error);
      setMessage({ type: 'error', text: t('config.templateApplyError', { error: error.message }) });
      setSaving(false);
    }
  };

  if (!user || !config) {
    return <div className="loading">{tc('loading')}</div>;
  }

  const tabs = [
    { id: 'general', label: t('config.tabs.general'), icon: '⚙️' },
    { id: 'purpose', label: t('config.tabs.purpose'), icon: '🎯' },
    { id: 'strategy', label: t('config.tabs.strategy'), icon: '⚡' },
    { id: 'playbooks', label: t('config.tabs.playbooks'), icon: '💬' },
    { id: 'knowledge', label: t('config.tabs.knowledge'), icon: '📚' },
    { id: 'repository', label: t('config.tabs.repository'), icon: '📁' },
    { id: 'tools', label: t('config.tabs.tools'), icon: '🔧' },
    { id: 'integrations', label: t('config.tabs.integrations'), icon: '🔌' },
  ];

  const unsavedBadge = dirty ? (
    <span style={{ marginLeft: '12px', color: '#d97706', fontSize: '0.875rem', fontWeight: 600 }}>
      ● {t('config.unsavedChanges')}
    </span>
  ) : null;

  return (
    <div className="bot-config-container">
      <PageHeader title={config.name || t('title')} backTo="/bots" />

      <BotStatusBanner
        config={config}
        runtime={runtime}
        playbooks={playbooks}
        onActivate={() => activateBot()}
      />

      {message && (
        <div className={`message-bar ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="config-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="config-content">
        {activeTab === 'general' && (
          <div className="tab-panel">
            <h2>{t('config.generalTitle')}</h2>

            <div className="form-group">
              <label>{t('config.botNameLabel')}</label>
              <input
                type="text"
                value={config.name || ''}
                onChange={e => updateConfig('name', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>{t('config.descriptionLabel')}</label>
              <textarea
                value={config.description || ''}
                onChange={e => updateConfig('description', e.target.value)}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>{t('config.toneLabel')}</label>
              <select
                value={config.tone || ''}
                onChange={e => updateConfig('tone', e.target.value)}
              >
                <option value="profesional y amable">{t('tones.profesional y amable')}</option>
                <option value="casual y cercano">{t('tones.casual y cercano')}</option>
                <option value="formal y técnico">{t('tones.formal y técnico')}</option>
                <option value="divertido y energético">{t('tones.divertido y energético')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('config.languageLabel')}</label>
              <select
                value={config.language || 'es'}
                onChange={e => updateConfig('language', e.target.value)}
              >
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="pt">Português</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('config.timezoneLabel')}</label>
              {/* Zonas desde SELECTABLE_TIMEZONES (fuente única timezoneMap).
                  Solo AGREGA opciones vs la lista vieja de 6 — la selección
                  guardada de un bot existente no se toca.
                  El valor es la zona EFECTIVA (2-nov-2026, `zonaEfectiva`): la del bot, si no la heredada de la
                  organización, si no la de por defecto, como el servidor. Mostrarla no la guarda. */}
              {(() => {
                const efectiva = zonaEfectiva({ zonaDelBot: config.timeZone || null, zonaDeLaOrg });
                const opciones = SELECTABLE_TIMEZONES.some((z) => z.value === efectiva.zona)
                  ? SELECTABLE_TIMEZONES
                  : [{ value: efectiva.zona, label: efectiva.zona }, ...SELECTABLE_TIMEZONES];
                return (
                  <>
                    <select
                      value={efectiva.zona}
                      onChange={e => updateConfig('timeZone', e.target.value)}
                    >
                      {opciones.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    {efectiva.origen !== 'bot' && (
                      <small style={{ color: '#b7791f', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                        {t(efectiva.origen === 'organizacion' ? 'config.timezoneInherited' : 'config.timezoneDefault')}
                      </small>
                    )}
                  </>
                );
              })()}
              <small style={{ color: '#718096', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                {t('config.timezoneHelp')}
              </small>
            </div>

            <button
              onClick={saveConfig}
              disabled={saving}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '16px',
                marginTop: '1rem'
              }}
            >
              {saving ? tc('saving') : t('config.saveConfig')}
            </button>
            {unsavedBadge}
          </div>
        )}

        {activeTab === 'purpose' && (
          <div className="tab-panel">
            <h2>{t('config.purposeTitle')}</h2>

            <div className="form-group">
              <label>{t('config.purposeLabel')}</label>
              <textarea
                value={config.purpose || ''}
                onChange={e => updateConfig('purpose', e.target.value)}
                rows={4}
                placeholder={t('config.purposePlaceholder')}
              />
            </div>

            {/* EL PRIMER MENSAJE FIJO (3-nov-2026): lo manda el sistema en el primer turno, sin el modelo
                (`functions/bot/utils/primerMensaje.js`). Vacío = como siempre. */}
            <div className="form-group">
              <label>{t('config.primerMensajeLabel')}</label>
              <textarea
                value={config.primerMensaje || ''}
                onChange={e => updateConfig('primerMensaje', e.target.value)}
                rows={3}
                placeholder={t('config.primerMensajePlaceholder')}
              />
              <small style={{ color: '#718096' }}>{t('config.primerMensajeHelp')}</small>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={config.primerMensajeOmitirSiNombraTipo === true}
                  onChange={e => updateConfig('primerMensajeOmitirSiNombraTipo', e.target.checked)}
                />
                {t('config.primerMensajeOmitirSiNombraTipo')}
              </label>
            </div>

            <PoliciesForm
              policy={tenantPolicy}
              onUpdate={updatePolicy}
            />

            <button
              onClick={saveConfig}
              disabled={saving}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '16px',
                marginTop: '1rem'
              }}
            >
              {saving ? tc('saving') : t('config.saveConfig')}
            </button>
            {unsavedBadge}
          </div>
        )}

        {activeTab === 'strategy' && (
          <div className="tab-panel">
            <h2>{t('config.strategyTitle')}</h2>
            <p style={{ color: '#718096', fontSize: '0.875rem', marginBottom: '2rem' }}>
              {t('config.strategyDescription')}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
              {getAllSalesFrameworks().map(framework => (
                <div
                  key={framework.id}
                  onClick={() => updateConfig('salesFramework', framework.id)}
                  style={{
                    padding: '1.5rem',
                    border: config.salesFramework === framework.id ? '3px solid #667eea' : '2px solid #e2e8f0',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    backgroundColor: config.salesFramework === framework.id ? '#eef2ff' : '#fff',
                    transition: 'all 0.2s',
                    boxShadow: config.salesFramework === framework.id ? '0 4px 12px rgba(102, 126, 234, 0.15)' : 'none'
                  }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>{framework.icon}</div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#2d3748' }}>{framework.name}</h3>
                  <p style={{ fontSize: '0.875rem', color: '#718096', margin: '0 0 1rem 0', lineHeight: '1.5' }}>
                    {framework.description}
                  </p>
                  <div style={{ fontSize: '0.75rem', color: '#4a5568' }}>
                    <strong>{t('config.idealFor')}</strong>
                    <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
                      {framework.bestFor.slice(0, 3).map((use, i) => (
                        <li key={i}>{use}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {config.salesFramework && (
              <div style={{
                marginTop: '2rem',
                padding: '1.5rem',
                backgroundColor: '#f7fafc',
                border: '2px solid #667eea',
                borderRadius: '12px'
              }}>
                <h3 style={{ marginTop: 0, color: '#2d3748' }}>
                  {t('config.selectedFramework', { name: getSalesFramework(config.salesFramework)?.name })}
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '1rem' }}>
                  {getSalesFramework(config.salesFramework)?.description}
                </p>
                <div style={{ fontSize: '0.875rem', color: '#4a5568' }}>
                  <strong>{t('config.recommendedUseCases')}</strong>
                  <ul style={{ marginTop: '0.5rem' }}>
                    {getSalesFramework(config.salesFramework)?.bestFor.map((use, i) => (
                      <li key={i}>{use}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <button
              onClick={saveConfig}
              disabled={saving}
              style={{
                padding: '14px 28px',
                backgroundColor: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '16px',
                marginTop: '2rem',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.25)'
              }}
            >
              {saving ? tc('saving') : t('config.saveStrategy')}
            </button>
            {unsavedBadge}
          </div>
        )}

        {activeTab === 'knowledge' && (
          <div className="tab-panel">
            <KnowledgeForm
              knowledge={knowledge || {}}
              onChange={(newKnowledge) => {
                setDirty(true);
                setConfirmarLimites(false);
                setKnowledge(newKnowledge);
              }}
            />
            {confirmarLimites && (
              <div role="alertdialog" aria-labelledby="confirmar-limites-titulo" style={{
                marginTop: '1rem', padding: '1rem', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', color: '#7f1d1d', maxWidth: '720px',
              }}>
                <strong id="confirmar-limites-titulo">{t('knowledge.limites.confirmarTitulo')}</strong>
                <p style={{ margin: '0.5rem 0 0.75rem' }}>{t('knowledge.limites.confirmarTexto')}</p>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setConfirmarLimites(false)}
                    style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #b91c1c', background: '#fff', color: '#b91c1c', fontWeight: 600, cursor: 'pointer' }}>
                    {t('knowledge.limites.volverAEditar')}
                  </button>
                  <button type="button" onClick={() => { setConfirmarLimites(false); saveConfig(); }}
                    style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', background: '#b91c1c', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                    {t('knowledge.limites.guardarIgual')}
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={() => (limitesDelConocimiento(knowledge || {}).hayExceso ? setConfirmarLimites(true) : saveConfig())}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '16px',
                marginTop: '1rem'
              }}
            >
              {saving ? tc('saving') : t('config.saveKnowledge')}
            </button>
            {unsavedBadge}
          </div>
        )}

        {activeTab === 'repository' && (
          <div className="tab-panel">
            <KnowledgeDocuments botId={botId} organizationId={context.mode === 'organization' ? context.orgId : null} />
            <BibliotecaDeMedios botId={botId} organizationId={context.mode === 'organization' ? context.orgId : null} context={context} />
          </div>
        )}

        {activeTab === 'playbooks' && (
          <div className="tab-panel">
            <h2 style={{ marginBottom: '0.5rem' }}>{t('config.playbooks.title')}</h2>
            <p style={{ color: '#718096', fontSize: '0.95rem', marginBottom: '2rem', maxWidth: '800px' }} dangerouslySetInnerHTML={{ __html: t('config.playbooks.description') }} />

            {/* 🚀 TEMPLATES POR INDUSTRIA */}
            <div style={{ marginBottom: '3rem' }}>
              <h3 style={{ color: '#2d3748', marginBottom: '0.5rem' }}>{t('config.playbooks.templatesTitle')}</h3>
              <p style={{ color: '#718096', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                {t('config.playbooks.templatesDescription')}
                <span style={{ display: 'block', marginTop: '0.5rem', color: '#f59e0b', fontWeight: 500 }}>
                  {t('config.playbooks.templatesWarning')}
                </span>
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(250px, 100%), 1fr))',
                gap: '1rem',
                marginTop: '1rem'
              }}>
                {getAllIndustries().map(industry => (
                  <button
                    key={industry.id}
                    onClick={() => applyTemplate(industry.id)}
                    disabled={saving}
                    style={{
                      background: 'white',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.currentTarget.style.borderColor = '#667eea';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <span style={{ fontSize: '3rem', display: 'block', marginBottom: '0.5rem' }}>
                      {industry.icon}
                    </span>
                    <h3 style={{ margin: '0.5rem 0', color: '#2d3748', fontSize: '1.1rem' }}>
                      {industry.name}
                    </h3>
                    <p style={{ margin: 0, color: '#718096', fontSize: '0.875rem' }}>
                      {industry.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <hr style={{ margin: '3rem 0', border: 'none', borderTop: '2px solid #e2e8f0' }} />

            {/* FLUJOS PERSONALIZADOS */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ color: '#2d3748', marginBottom: '0.5rem' }}>{t('config.playbooks.customTitle')}</h3>
                <p style={{ color: '#718096', fontSize: '0.875rem', margin: 0, maxWidth: '600px' }}>
                  {t('config.playbooks.customDescription')}
                </p>
              </div>
              <button
                onClick={createPlaybook}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#9c27b0',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  boxShadow: '0 2px 8px rgba(156, 39, 176, 0.25)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(156, 39, 176, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(156, 39, 176, 0.25)';
                }}
              >
                {t('config.playbooks.createCustom')}
              </button>
            </div>

            {playbooks.length === 0 && (
              <div style={{
                padding: '2rem',
                backgroundColor: '#f7fafc',
                border: '2px dashed #cbd5e0',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#718096'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>💬</div>
                <p style={{ margin: 0, fontSize: '0.875rem' }} dangerouslySetInnerHTML={{ __html: t('config.playbooks.empty') }} />
              </div>
            )}

            {playbooks.map(playbook => (
              <PlaybookEditor
                key={playbook.id}
                playbook={playbook}
                botId={botId}
                isActivePlaybook={playbook.id === runtime?.activePlaybookId}
                onActivate={() => activateBot(playbook.id)}
                onDelete={handlePlaybookDeleted}
              />
            ))}
          </div>
        )}

        {activeTab === 'tools' && (
          <div className="tab-panel">
            <h2>{t('config.toolsTitle')}</h2>
            <p className="help-text">
              {t('config.toolsHelp')}
            </p>

            <ToolsAllowlist
              allowed={tenantPolicy?.tools_allowed || []}
              onChange={tools => updatePolicy('tools_allowed', tools)}
            />

            {/* EL ORDEN DE LOS ARCHIVOS (4-nov-2026): el de los tipos al enviar lo que pidió el modelo
                (`functions/bot/media/envioDeArchivos.js`, `ordenDeTipos`). Sin elegir, documento → fotos → audio. */}
            <div className="form-group">
              <label htmlFor="orden-de-archivos">{t('config.ordenDeArchivosLabel')}</label>
              <select
                id="orden-de-archivos"
                value={(Array.isArray(config.ordenDeArchivos) ? config.ordenDeArchivos : ORDENES_DE_ARCHIVOS[0]).join(',')}
                onChange={e => updateConfig('ordenDeArchivos', e.target.value.split(','))}
              >
                {ORDENES_DE_ARCHIVOS.map(orden => (
                  <option key={orden.join(',')} value={orden.join(',')}>
                    {orden.map(tipo => t(`config.ordenDeArchivosTipo.${tipo}`)).join(' → ')}
                  </option>
                ))}
              </select>
              <small style={{ color: '#718096' }}>{t('config.ordenDeArchivosHelp')}</small>
            </div>

            <div className="info-box">
              {t('config.toolsNote')}
            </div>

            <button
              onClick={saveConfig}
              disabled={saving}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '16px',
                marginTop: '1rem'
              }}
            >
              {saving ? tc('saving') : t('config.saveTools')}
            </button>
            {unsavedBadge}
          </div>
        )}

        {activeTab === 'integrations' && (
          <div className="tab-panel">
            <BotIntegrationsPanel
              user={user}
              botId={botId}
              refreshTrigger={refreshIntegrations}
              context={context}
            />
          </div>
        )}

      </div>

      <style>{`
        .bot-config-container {
          max-width: 1200px;
          margin: 2rem auto;
          padding: 2rem;
        }

        .message-bar {
          padding: 1rem;
          border-radius: 6px;
          margin-bottom: 1rem;
          font-weight: 500;
        }

        .message-bar.success {
          background: #f0fff4;
          color: #22543d;
          border: 1px solid #9ae6b4;
        }

        .message-bar.error {
          background: #fff5f5;
          color: #742a2a;
          border: 1px solid #fc8181;
        }

        .config-tabs {
          display: flex;
          gap: 0.25rem;
          border-bottom: 2px solid #e2e8f0;
          margin-bottom: 2rem;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }

        .tab {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 1.25rem;
          border: none;
          background: transparent;
          cursor: pointer;
          color: #718096;
          font-size: 0.95rem;
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .tab:hover {
          color: #2d3748;
          background: #f7fafc;
        }

        .tab.active {
          color: #667eea;
          border-bottom-color: #667eea;
          font-weight: 600;
        }

        .tab-icon {
          font-size: 1.25rem;
        }

        @media (max-width: 768px) {
          .tab {
            padding: 0.75rem 0.875rem;
            font-size: 0.85rem;
            flex-direction: row;
            gap: 0.4rem;
          }

          .tab-label {
            font-size: 0.85rem;
            white-space: nowrap;
          }

          .tab-icon {
            font-size: 1rem;
          }

          .config-content {
            padding: 1rem;
          }
        }

        @media (max-width: 480px) {
          .bot-config-container {
            padding: 0.75rem;
          }

          .tab {
            padding: 0.625rem 0.75rem;
            font-size: 0.8rem;
          }

          .tab-label {
            font-size: 0.8rem;
          }

          .tab-icon {
            font-size: 1rem;
          }

          .config-content {
            padding: 1rem;
          }
        }

        .config-content {
          background: white;
          border-radius: 8px;
          padding: 2rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .tab-panel h2 {
          margin-top: 0;
          color: #2d3748;
        }

        .help-text {
          color: #718096;
          margin-bottom: 1.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #2d3748;
        }

        .form-group input,
        .form-group textarea,
        .form-group select {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 1rem;
        }

        .form-group input:focus,
        .form-group textarea:focus,
        .form-group select:focus {
          outline: none;
          border-color: #667eea;
        }

        .info-box {
          background: #ebf8ff;
          border: 1px solid #bee3f8;
          border-radius: 6px;
          padding: 1rem;
          color: #2c5282;
          font-size: 0.875rem;
          margin-top: 1.5rem;
        }

        .empty-state {
          text-align: center;
          padding: 3rem;
          color: #a0aec0;
          font-style: italic;
        }

        .saving-indicator {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          background: #667eea;
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 6px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          animation: fadeIn 0.3s;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .loading {
          text-align: center;
          padding: 3rem;
          color: #718096;
        }
      `}</style>
    </div>
  );
}
